//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as instagramHashtagsApi from '@apis/instagram-hashtag/instagramHashtagApi';
import { IInstagramHashtagMediaResponse } from '@apis/instagram-hashtag/instagramHashtagApiModel';
import {
  generateInstagramSearchRequestBody,
  transformAutocompleteData,
} from '@instagram-account/instagramSharedUtils';
import { IInstagramSourceAndCountWithAutocomplete } from '@instagram-account/models/IInstagramAutocompleteResponses';
import {
  IInstagramHashtagRecentContentState,
  RECENT_CONTENT_REQUEST_LIMIT,
} from '@instagram-account/redux/instagram-hashtag/recent-content/instagramHashtagRecentContentModel';
import { FilterMatchState } from '@models/FilterMatchState';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';

const initialState: IInstagramHashtagRecentContentState = {
  autocompleteResults: [],
  errors: {
    fetchInstagramHashtagAutocompleteError: undefined,
    fetchInstagramHashtagRecentContentError: undefined,
    fetchInstagramRelatedHashtagsError: undefined,
    fetchMoreInstagramHashtagRecentContentError: undefined,
  },
  filterState: {
    searchTerms: [],
    sort: UniversalSearchSortKey.PostedTimestamp,
  },
  flags: {
    isEndOfInstagramHashtagRecentContentReached: false,
    isFetchingInstagramHashtagAutocomplete: false,
    isFetchingInstagramHashtagRecentContent: false,
    isFetchingInstagramRelatedHashtags: false,
    isFetchingMoreInstagramHashtagRecentContent: false,
  },
  items: [],
  offset: 0,
  relatedHashtags: [],
  totalMediaCount: 0,
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramHashtagRecentContent,
  initialState,
  reducers: {
    setInstagramHashtagRecentContentSort(
      state,
      action: PayloadAction<UniversalSearchSortKey>,
    ) {
      state.filterState.sort = action.payload;
    },
    addInstagramHashtagSearchTerm(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete>,
    ) {
      state.filterState.searchTerms = [
        ...state.filterState.searchTerms,
        action.payload,
      ];
    },
    setInstagramHashtagSearchTerms(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete[]>,
    ) {
      state.filterState.searchTerms = [...action.payload];
    },
    requestInstagramHashtagRecentContent(state) {
      state.flags.isFetchingInstagramHashtagRecentContent = true;
    },
    receiveInstagramHashtagRecentContentSucceed(
      state,
      action: PayloadAction<IInstagramHashtagMediaResponse>,
    ) {
      state.flags.isEndOfInstagramHashtagRecentContentReached =
        action.payload.data.media.length < RECENT_CONTENT_REQUEST_LIMIT;
      state.flags.isFetchingInstagramHashtagRecentContent = false;
      state.items = [...action.payload.data.media];
      state.offset = action.payload.data.offset;
      state.totalMediaCount = action.payload.data.totalCount;
    },
    receiveInstagramHashtagRecentContentFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramHashtagRecentContent = false;
      state.errors.fetchInstagramHashtagRecentContentError = action.payload;
    },
    requestMoreInstagramHashtagRecentContent(state) {
      state.flags.isFetchingMoreInstagramHashtagRecentContent = true;
    },
    receiveMoreInstagramHashtagRecentContentSucceed(
      state,
      action: PayloadAction<{
        content: IMlsMediaItem[];
        offset: number;
      }>,
    ) {
      state.flags.isEndOfInstagramHashtagRecentContentReached =
        action.payload.content.length < RECENT_CONTENT_REQUEST_LIMIT;
      state.flags.isFetchingMoreInstagramHashtagRecentContent = false;
      state.items = [...state.items, ...action.payload.content];
      state.offset = action.payload.offset;
    },
    receiveMoreInstagramHashtagRecentContentFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingMoreInstagramHashtagRecentContent = false;
      state.errors.fetchMoreInstagramHashtagRecentContentError = action.payload;
    },
    requestInstagramHashtagAutocomplete(state) {
      state.flags.isFetchingInstagramHashtagAutocomplete = true;
    },
    receiveInstagramHashtagAutocompleteSucceed(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete[]>,
    ) {
      state.flags.isFetchingInstagramHashtagAutocomplete = false;
      state.autocompleteResults = [...action.payload];
    },
    receiveInstagramHashtagAutocompleteFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramHashtagAutocomplete = false;
      state.errors.fetchInstagramHashtagAutocompleteError = action.payload;
    },
    requestInstagramRelatedHashtags(state) {
      state.flags.isFetchingInstagramRelatedHashtags = true;
    },
    receiveInstagramRelatedHashtagsSucceed(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete[]>,
    ) {
      state.flags.isFetchingInstagramRelatedHashtags = false;
      state.relatedHashtags = [...action.payload];
    },
    receiveInstagramRelatedHashtagsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramRelatedHashtags = false;
      state.errors.fetchInstagramRelatedHashtagsError = action.payload;
    },
    resetContentState(state) {
      state.offset = 0;
      state.items = [];
      state.flags.isEndOfInstagramHashtagRecentContentReached = false;
    },
    resetHashtagRecentContentState(state) {
      return initialState;
    },
  },
});

const getRecentContent = createThunk(() => async (dispatch, getState) => {
  const { dateRange, hashtag } = getState().instagram.hashtags.page;
  const {
    filterState: { searchTerms, sort },
    offset,
  } = getState().instagram.hashtags.recentContent;

  const searchedBody = generateInstagramSearchRequestBody(searchTerms);

  return instagramHashtagsApi.getRecentContent(
    {
      hashtag,
      matchState: FilterMatchState.All,
      startTime: dateRange.startDate,
      endTime: dateRange.endDate,
      offset,
      limit: RECENT_CONTENT_REQUEST_LIMIT,
      sortKey: sort,
    },
    searchedBody,
  );
});

export const fetchInstagramHashtagRecentContent = createThunk(
  () => async (dispatch, getState) => {
    const {
      flags: { isFetchingInstagramHashtagRecentContent },
    } = getState().instagram.hashtags.recentContent;

    if (isFetchingInstagramHashtagRecentContent) {
      return;
    }

    dispatch(actions.resetContentState());
    dispatch(actions.requestInstagramHashtagRecentContent());
    dispatch(fetchRelatedHashtagsForTrackedHashtag());

    try {
      const recentContentData = await dispatch(getRecentContent());

      dispatch(
        actions.receiveInstagramHashtagRecentContentSucceed(recentContentData),
      );
    } catch (_e) {
      dispatch(
        actions.receiveInstagramHashtagRecentContentFailed(
          'An error occurred while fetching recent content for this hashtag. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const fetchMoreInstagramHashtagRecentContent = createThunk(
  () => async (dispatch, getState) => {
    const {
      flags: {
        isEndOfInstagramHashtagRecentContentReached,
        isFetchingMoreInstagramHashtagRecentContent,
      },
    } = getState().instagram.hashtags.recentContent;

    if (
      isEndOfInstagramHashtagRecentContentReached ||
      isFetchingMoreInstagramHashtagRecentContent
    ) {
      return;
    }

    dispatch(actions.requestMoreInstagramHashtagRecentContent());

    try {
      const recentContentData = await dispatch(getRecentContent());

      dispatch(
        actions.receiveMoreInstagramHashtagRecentContentSucceed({
          content: recentContentData.data.media,
          offset: recentContentData.data.offset,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveMoreInstagramHashtagRecentContentFailed(
          'An error occurred while fetching more recent content for this hashtag. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const generateSummaryOfRecentContent = createThunk(
  () => async (dispatch, getState) => {
    const { dateRange, hashtag } = getState().instagram.hashtags.page;
    const {
      filterState: { searchTerms, sort },
    } = getState().instagram.hashtags.recentContent;

    const searchedBody = generateInstagramSearchRequestBody(searchTerms);

    return instagramHashtagsApi.getRecentContentZip(
      {
        hashtag,
        matchState: FilterMatchState.All,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
        sortKey: sort,
      },
      searchedBody,
    );
  },
);

export const setSortAndRefreshContent = createThunk(
  (sort: UniversalSearchSortKey) => async (dispatch, getState) => {
    dispatch(actions.setInstagramHashtagRecentContentSort(sort));
    dispatch(fetchInstagramHashtagRecentContent());
  },
);

export const setFilterAndRefreshContent = createThunk(
  (filterOption: IInstagramSourceAndCountWithAutocomplete) =>
    async (dispatch, getState) => {
      const {
        filterState: { searchTerms },
      } = getState().instagram.hashtags.recentContent;

      const hasSearchTerm = searchTerms.some(
        (term) => term.source === filterOption.source,
      );

      if (hasSearchTerm) {
        return;
      }

      dispatch(actions.addInstagramHashtagSearchTerm(filterOption));
      dispatch(fetchInstagramHashtagRecentContent());
    },
);

export const removeFilterByIndexAndRefreshContent = createThunk(
  (index: number) => async (dispatch, getState) => {
    const {
      filterState: { searchTerms },
    } = getState().instagram.hashtags.recentContent;

    const updatedSearchTerms = [
      ...searchTerms.slice(0, index),
      ...searchTerms.slice(index + 1),
    ];

    dispatch(actions.setInstagramHashtagSearchTerms(updatedSearchTerms));
    dispatch(fetchInstagramHashtagRecentContent());
  },
);

export const removeAllFiltersAndRefreshContent = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.setInstagramHashtagSearchTerms([]));
    dispatch(fetchInstagramHashtagRecentContent());
  },
);

export const fetchHashtagAutocomplete = createThunk(
  (searchTerm: string = '') =>
    async (dispatch, getState) => {
      const { dateRange, hashtag } = getState().instagram.hashtags.page;
      const {
        filterState: { searchTerms },
      } = getState().instagram.hashtags.recentContent;

      dispatch(actions.requestInstagramHashtagAutocomplete());

      try {
        const searchedBody = generateInstagramSearchRequestBody(searchTerms);

        const autocompleteData =
          await instagramHashtagsApi.getHashtagAutocompleteSuggestions(
            {
              hashtag,
              searchTerm,
              matchState: FilterMatchState.All,
              startTime: dateRange.startDate,
              endTime: dateRange.endDate,
            },
            searchedBody,
          );

        const autocompleteResults = transformAutocompleteData(
          autocompleteData.data,
        );

        dispatch(
          actions.receiveInstagramHashtagAutocompleteSucceed(
            autocompleteResults,
          ),
        );
      } catch (_e) {
        dispatch(
          actions.receiveInstagramHashtagAutocompleteFailed(
            'An error occurred while fetching hashtag autocomplete suggestions. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
);

export const fetchRelatedHashtagsForTrackedHashtag = createThunk(
  () => async (dispatch, getState) => {
    const { dateRange, hashtag } = getState().instagram.hashtags.page;
    const {
      filterState: { searchTerms },
    } = getState().instagram.hashtags.recentContent;

    dispatch(actions.requestInstagramRelatedHashtags());

    try {
      const searchedBody = generateInstagramSearchRequestBody(searchTerms);

      const relatedHashtagsData =
        await instagramHashtagsApi.getRelatedHashtagsForTrackedHashtag(
          {
            hashtag,
            matchState: FilterMatchState.All,
            startTime: dateRange.startDate,
            endTime: dateRange.endDate,
          },
          searchedBody,
        );

      const transformedRelatedHashtags = transformAutocompleteData({
        hashtags: relatedHashtagsData.data,
        usernames: [],
        labels: [],
      });

      dispatch(
        actions.receiveInstagramRelatedHashtagsSucceed(
          transformedRelatedHashtags,
        ),
      );
    } catch (_e) {
      dispatch(
        actions.receiveInstagramRelatedHashtagsFailed(
          'An error occurred while fetching related hashtags. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const { resetHashtagRecentContentState } = actions;

export default reducer;
